import * as React from "react"
import {
Dialog,
DialogContent,
DialogDescription,
DialogFooter,
DialogHeader,
DialogTitle,
} from "@/components/ui/dialog"
import { Button,buttonVariants } from "@/components/ui/button"
import { type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface FormDialogProps {
  /** Controls whether the dialog is open */
  open: boolean
  /** Callback for when the dialog's open state changes */
  onOpenChange: (open: boolean) => void
  /** The title of the dialog */
  title: React.ReactNode
  /** Optional description for the dialog */
  description?: React.ReactNode
  /** The content of the dialog */
  children: React.ReactNode
  /** Label for the confirmation button. Defaults to "Save Changes" */
  confirmLabel?: string
  /** Custom variant for the confirmation button. Defaults to "default" */
  confirmVariant?: VariantProps<typeof buttonVariants>["variant"]

  /** Label for the cancel button. Defaults to "Cancel" */
  cancelLabel?: string
  /** Callback for when the confirmation button is clicked */
  onConfirm: () => void | Promise<void>
  /** Callback for when the cancel button is clicked or dialog closed */
  onCancel?: () => void
  /** Whether the action is in progress (shows loader and disables buttons) */
  isPending?: boolean
  /** Whether the confirmation button should be disabled manually */
  isConfirmDisabled?: boolean
  /** Width preset for the dialog */
  size?: "slim" | "medium" | "large" | "x-large" | "xx-large" | "huge"
  /** Custom class name for the dialog content wrapper */
  className?: string
  /** Custom class name for the inner scrollable body */
  contentClassName?: string
  /** Standard Dialog onOpenAutoFocus prop */
  onOpenAutoFocus?: React.ComponentProps<typeof DialogContent>["onOpenAutoFocus"]
  /** Whether to hide the default footer (useful for view-only or custom footers) */
  hideFooter?: boolean
  /** Optional custom content to display in the footer (left side of buttons) */
  footerSlot?: React.ReactNode
}

/**
 * A unified, reusable "pro-tool" design compliant form dialog.
 * Features a standardized header, scrollable body, and consistent footer with 
 * ghost cancel button and primary action button with loading states.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = "Save Changes",
  confirmVariant = "default",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  isPending = false,
  isConfirmDisabled = false,
  size = "medium",
  className,
  contentClassName,
  onOpenAutoFocus,
  hideFooter = false,
  footerSlot,
}: FormDialogProps) {
  const sizeClasses = {
    slim: "sm:max-w-[400px]",
    medium: "sm:max-w-[512px]",
    large: "sm:max-w-[576px]",
    "x-large": "sm:max-w-[768px]",
    "xx-large": "sm:max-w-[1024px]",
    "huge": "sm:max-w-[1280px]",
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          sizeClasses[size], 
          "p-0 overflow-hidden flex flex-col max-h-[95vh]",
          "[&>button]:top-[22px] [&>button]:right-[24px] [&>button]:rounded-md [&>button]:h-8 [&>button]:w-8 [&>button]:inline-flex [&>button]:items-center [&>button]:justify-center [&>button]:transition-all [&>button]:duration-200 [&>button]:hover:bg-muted [&>button]:hover:text-foreground [&>button]:opacity-60 [&>button]:hover:opacity-100 [&>button]:!ring-0 [&>button]:!ring-offset-0 [&>button]:!outline-none [&>button]:focus-visible:!ring-0 [&>button]:focus-visible:!outline-none",
          className
        )}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-xl leading-tight pr-6">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-muted-foreground mt-0.5">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        
        <div className={cn("px-6 pt-1 pb-1 flex-1 overflow-y-auto overflow-x-hidden min-h-0 text-sm", contentClassName)}>
          {children}
        </div>

        {!hideFooter && (
          <DialogFooter className="px-6 pt-4 pb-6 shrink-0">
            <div className="flex items-center justify-between w-full gap-4">
              <div className="flex-1">
                {footerSlot}
              </div>
              <div className="flex items-center gap-4 mt-2 sm:mt-0 sm:justify-end shrink-0">
                {/* A <span> here was unreachable by keyboard and invisible to
                    assistive tech — and this footer is now the terminal-wide
                    standard, so the flaw would have propagated to every modal.
                    Same visual, real button. */}
                <button
                  type="button"
                  onClick={() => {
                    onCancel?.()
                    onOpenChange(false)
                  }}
                  disabled={isPending}
                  className="rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  {cancelLabel}
                </button>
                <Button
                  type="submit"
                  variant={confirmVariant}
                  onClick={(e) => {
                    e.preventDefault()
                    void onConfirm()
                  }}
                  disabled={isPending || isConfirmDisabled}
                  className="min-w-[120px] shadow-sm"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
